import React, {
  memo,
  forwardRef,
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import moment from 'moment';
import debounce from 'lodash/debounce';
import { Form, Row, Col, Select, Input, InputNumber, DatePicker, Alert, Divider } from 'antd';
import event from '~helpers/mixpanel';
import Modal from 'common/components/modal';
import CounterOffer from '~components/counter-offer';
import { propertyListingForm } from '~constants/';
import { TagType } from '~constants/tagType';
import { orderCounterOfferDiscountType } from '~constants/bookings';
import handleTenancy from '~helpers/tenancy-preview';
import formatPrice from '~helpers/currency';
import { dayFormatType, calcTenancyLength } from 'common/helpers/day';
import { QUERY_UNIT_TYPES, QUERY_ORDER_PRICE, CHECK_COUNTER_OFFER_INSTALMENT } from '../queries';
import {
  OFFER_VALIDITY_PERIOD,
  moveInTypes,
  moveOutTypes,
  tenancyLengthTypes,
  getTenancyValues,
} from './utils';
import SecondConfirmModal from './confirm';
import CounterOfferNotice from '../notice';
import SelectPropertyModal from '../select-property-modal/';
import { getItem, removeItem } from '~helpers/storage';

import useStyles from './styles';

const CounterOfferModal = memo(
  forwardRef(({ form, booking, isAutoOpen, refetchBookingList }, ref) => {
    const {
      getFieldDecorator,
      getFieldValue,
      getFieldsValue,
      setFieldsValue,
      resetFields,
      validateFieldsAndScroll,
    } = form;

    const { t } = useTranslation();
    const classes = useStyles();

    const [visible, setVisible] = useState(false);
    const [record, setRecord] = useState({});
    const [currentRoom, setCurrentRoom] = useState(null);
    const [currentListing, setCurrentListing] = useState(null);
    const [counterOffer, setCounterOffer] = useState({});

    const [reselectProperty, setReselectProperty] = useState(null);

    const confirmModalRef = useRef(null);
    const selectPropertyRef = useRef(null);

    const [queryUnitTypes, { data: roomData, loading: roomLoading }] = useLazyQuery(
      QUERY_UNIT_TYPES,
      {
        fetchPolicy: 'no-cache',
        onCompleted: () => {
          if (!reselectProperty) {
            setFieldsValue({
              moveIn: moment(record.moveInDate),
              moveOut: moment(record.moveOutDate),
            });
          }
        },
      },
    );
    const [queryOrderPrice, { loading: priceLoading }] = useLazyQuery(QUERY_ORDER_PRICE, {
      fetchPolicy: 'no-cache',
      onCompleted: val => {
        if (val.calculateOrderPrice?.nonInstalments) {
          const { listingSalesPrice, listingSalesOriginPrice, totalPrice } =
            val.calculateOrderPrice.nonInstalments;

          setFieldsValue({
            originalPrice: listingSalesOriginPrice,
            currentPrice: listingSalesPrice,
            totalPrice,
          });
        }
      },
    });
    const [checkCounterOfferInstalment, { loading: instalmentLoading }] = useLazyQuery(
      CHECK_COUNTER_OFFER_INSTALMENT,
      {
        fetchPolicy: 'no-cache',
        onCompleted: val => {
          if (val.checkOrderCounterOfferInstalment) {
            confirmModalRef.current?.handleOpenModal(val.checkOrderCounterOfferInstalment.result);
          }
        },
      },
    );

    const handleOpenModal = useCallback(
      row => {
        setVisible(true);

        setRecord(row);
        setCurrentRoom(row.unitTypeId);
        setCurrentListing(row.listingId);

        queryUnitTypes({
          variables: {
            propertyId: row?.propertyId,
          },
        });

        queryOrderPrice({
          variables: {
            listingId: row.listingId,
            manualDiscountType: null,
            manualDiscountValue: null,
            moveInDate: moment(row.moveInDate)?.format(dayFormatType.FILTERPARAMS),
            moveOutDate: moment(row.moveOutDate)?.format(dayFormatType.FILTERPARAMS),
          },
        });
      },
      [queryUnitTypes, queryOrderPrice],
    );

    const handleReOpenModal = useCallback(property => {
      setReselectProperty(property);

      queryUnitTypes({
        variables: {
          propertyId: property.id,
        },
      });

      setCurrentRoom(null);
      setCurrentListing(null);

      setFieldsValue({
        room: undefined,
        listing: undefined,
        moveIn: undefined,
        moveOut: undefined,
        totalPrice: null,
        originalPrice: null,
        currentPrice: null,
        discountValue: null,
        message: null,
      });

      resetFields(['discountType', 'validPeriodHour']);
    });

    const handleCloseModal = useCallback(() => {
      setVisible(false);
      setRecord({});
      setCurrentRoom(null);
      setCurrentListing(null);
      setReselectProperty(null);

      resetFields([
        'moveIn',
        'moveOut',
        'discountType',
        'discountValue',
        'validPeriodHour',
        'message',
      ]);
    });

    const roomOptionData = useMemo(() => {
      return roomData?.listUnitTypes?.edges?.map(room => room.node) ?? [];
    }, [roomData]);

    const listingOptionData = useMemo(() => {
      return (
        roomOptionData
          ?.filter(room => room.id === currentRoom)?.[0]
          ?.listings?.filter(
            listing =>
              listing.type !== propertyListingForm.listingType.placeholder &&
              listing.availability !== propertyListingForm.availability.soldOut,
          ) ?? []
      );
    }, [roomOptionData, currentRoom]);

    const handleClickCounterSetTrack = position => {
      event('Clicked Counter Offer Settings', { 'Clicked Position': position });
    };
    const listingData = useMemo(() => {
      return listingOptionData?.filter(listing => listing.id === currentListing)?.[0] ?? {};
    }, [currentListing, listingOptionData]);

    const { isConfirmButtonDisabled, isDateModified } = (() => {
      const { moveIn, moveOut } = getFieldsValue();

      const modified =
        currentRoom !== record.unitTypeId ||
        currentListing !== record.listingId ||
        moveIn?.format(dayFormatType.FILTERPARAMS) !== record.moveInDate ||
        moveOut?.format(dayFormatType.FILTERPARAMS) !== record.moveOutDate;

      const isLoading = roomLoading || priceLoading;

      return {
        isConfirmButtonDisabled:
          isLoading || !modified || !moveIn || !moveOut || !currentRoom || !currentListing,
        isDateModified: modified,
      };
    })();

    const propertyBillingCycle = t(
      record.billingCycle === 'WEEKLY'
        ? 'landlord.listing.billing_cycle.per_weekly'
        : 'landlord.listing.billing_cycle.per_monthly',
    );

    const getMoveInDisabledDate = useCallback(
      current => {
        const currentDay = moment(current?.format(dayFormatType.FILTERPARAMS));
        const today = moment(moment().format(dayFormatType.FILTERPARAMS));
        let before = today;
        let after = null;

        const moveIn = listingData.moveIn ? moment(listingData.moveIn) : today;
        const moveOut = listingData.moveOut ? moment(listingData.moveOut) : today;
        const { moveInType, moveOutType, tenancyLengthType, tenancyLengthValue } = listingData;
        const { tenancyFrom } = getTenancyValues(tenancyLengthType, tenancyLengthValue);
        const tenancyUnit = record?.billingCycle?.toLowerCase() === 'weekly' ? 'week' : 'month';
        // get moveIn before
        before = moveIn?.diff(today, 'day') >= 0 ? moveIn : today;

        // get moveIn after
        if (moveInType === moveInTypes.EXACTLY_MATCH) {
          after =
            moveIn && moment(moveIn).diff(today, 'day') > 0
              ? moment(moveIn).add(7, 'day')
              : today.add(7, 'day');
        } else if (moveOutType === moveOutTypes.ANYTIME) {
          after = null;
        } else if (moveOutType === moveOutTypes.BEFORE) {
          after = moveOut?.subtract(tenancyFrom, tenancyUnit);
        }

        return (
          currentDay?.isBefore(moment(before).format(dayFormatType.FILTERPARAMS)) ||
          currentDay?.isSameOrAfter(moment(after).format(dayFormatType.FILTER))
        );
      },
      [listingData],
    );

    const getMoveOutDisabledDate = useCallback(
      current => {
        const currentDay = moment(current?.format(dayFormatType.FILTERPARAMS));
        const today = moment(moment().format(dayFormatType.FILTERPARAMS));
        let before = null;
        let after = null;

        const moveIn = getFieldsValue()?.moveIn;
        const moveOut = listingData.moveOut ? moment(listingData.moveOut) : today;
        const { moveInType, moveOutType, tenancyLengthType, tenancyLengthValue } = listingData;

        if (
          moveInType === moveInTypes.EXACTLY_MATCH &&
          moveOutType === moveOutTypes.EXACTLY_MATCH &&
          tenancyLengthType === tenancyLengthTypes.EQUALS
        ) {
          before = moment(moveOut).format('YYYY-MM-DD');
          after = moment(moveOut).format('YYYY-MM-DD');
        } else {
          const { tenancyFrom, tenancyEnd } = getTenancyValues(
            tenancyLengthType,
            tenancyLengthValue,
          );
          const tenancyUnit = record?.billingCycle?.toLowerCase() === 'weekly' ? 'week' : 'month';

          // get moveOut before
          if (tenancyFrom > 0) {
            before = moment(moveIn).add(tenancyFrom, tenancyUnit);
          } else {
            before = moment(moveIn).add(1, 'day');
          }

          // get moveOut after
          const moveOutLimit = moveOutType === moveOutTypes.BEFORE ? moment(moveOut) : null;
          const tanancyOutLimit = tenancyEnd ? moment(moveIn).add(tenancyEnd, tenancyUnit) : null;

          if (moveOutLimit && tanancyOutLimit) {
            after = moment.min(moveOutLimit, tanancyOutLimit);
          } else if (!moveOutLimit && tanancyOutLimit) {
            after = tanancyOutLimit;
          } else if (moveOutLimit && !tanancyOutLimit) {
            after = moveOutLimit;
          } else {
            after = null;
          }
        }

        if (!current) {
          return before;
        }

        return (
          currentDay.isSame(moment(moveIn)) ||
          currentDay?.isBefore(moment(before).format(dayFormatType.FILTERPARAMS)) ||
          currentDay?.isAfter(moment(after).format(dayFormatType.FILTER))
        );
      },
      [listingData],
    );

    const handleRoomChange = useCallback(value => {
      setCurrentRoom(value);
      setCurrentListing(null);

      setFieldsValue({
        listing: undefined,
        moveIn: undefined,
        moveOut: undefined,
      });
    });

    const handleListingChange = useCallback(value => {
      setCurrentListing(value);

      setFieldsValue({
        moveIn: undefined,
        moveOut: undefined,
      });
    });

    const handleMoveInChange = useCallback(() => {
      handleClickCounterSetTrack('Move-in Date');

      setFieldsValue({
        moveOut: undefined,
      });
    });

    const handleMoveOutChange = useCallback(
      moveOut => {
        const { moveIn, discountType, discountValue } = getFieldsValue();

        handleClickCounterSetTrack('Move-out Date');

        queryOrderPrice({
          variables: {
            listingId: currentListing,
            manualDiscountType: discountType ?? null,
            manualDiscountValue: discountValue ? String(discountValue)?.replace('%', '') : null,
            moveInDate: moveIn?.format(dayFormatType.FILTERPARAMS),
            moveOutDate: moveOut?.format(dayFormatType.FILTERPARAMS),
          },
        });
      },
      [currentListing],
    );

    const handleDiscountTypeChange = useCallback(
      val => {
        const { moveIn, moveOut } = getFieldsValue();

        resetFields(['discountValue']);

        if (val === null) {
          queryOrderPrice({
            variables: {
              listingId: currentListing,
              manualDiscountType: null,
              manualDiscountValue: null,
              moveInDate: moment(moveIn)?.format(dayFormatType.FILTERPARAMS),
              moveOutDate: moment(moveOut)?.format(dayFormatType.FILTERPARAMS),
            },
          });
        }
      },
      [currentListing],
    );

    const handleDiscountValueChange = useCallback(
      discountValue => {
        const { moveIn, moveOut, discountType } = getFieldsValue();

        queryOrderPrice({
          variables: {
            listingId: currentListing,
            manualDiscountType: discountType ?? null,
            manualDiscountValue: discountValue ? String(discountValue)?.replace('%', '') : null,
            moveInDate: moveIn?.format(dayFormatType.FILTERPARAMS),
            moveOutDate: moveOut?.format(dayFormatType.FILTERPARAMS),
          },
        });
      },
      [currentListing],
    );

    const handleDiscountValueChangeDebounce = debounce(handleDiscountValueChange, 300);

    const handleConfirmCreateOffer = useCallback(() => {
      validateFieldsAndScroll((err, values) => {
        if (!err) {
          const {
            moveIn,
            moveOut,
            discountType,
            discountValue,
            validPeriodHour,
            totalPrice,
            message = null,
          } = values;

          const moveInDate = moveIn?.format(dayFormatType.FILTERPARAMS);
          const moveOutDate = moveOut?.format(dayFormatType.FILTERPARAMS);

          const roomData = roomOptionData?.find(item => item.id === currentRoom) ?? {};

          const params = {
            moveInDate,
            moveOutDate,
            id: record.id,
            propertyId: record?.property?.id,
          };

          const newCounterOffer = {
            id: record.id,
            listingId: currentListing,
            discountType,
            discountValue: discountValue ? String(discountValue) : null,
            moveInDate,
            moveOutDate,
            validPeriodHour,
            message,
            totalPrice,
            propertyId: record?.property?.id,
            unitTypeName: roomData.name,
            unitTypeCategory: roomData.category,
          };

          if (reselectProperty) {
            params.propertyId = reselectProperty.id;

            newCounterOffer.propertyId = reselectProperty.id;
            newCounterOffer.propertyName = reselectProperty.name;
          }

          setCounterOffer(newCounterOffer);

          checkCounterOfferInstalment({
            variables: {
              input: params,
            },
          });
        }
      });
    }, [record, currentListing, roomData]);

    const handleOpenPropertyModal = () => {
      event('Clicked Counter Offer Settings', { 'Clicked Position': 'Property' });
      selectPropertyRef?.current?.handleOpenModal();
    };

    const handleViewEventTrack = () => {
      if (getItem('counterOfferFrom')) {
        event('Viewed New Counter Offer Page', { 'Auth Method': getItem('counterOfferFrom') });
        removeItem('counterOfferFrom');
      }
    };
    useImperativeHandle(ref, () => ({
      handleOpenModal,
    }));

    useEffect(() => {
      if (isAutoOpen) {
        event('Viewed New Counter Offer Page', { 'Auth Method': 'Livechat Page' });
        handleOpenModal(booking);
      }
    }, [isAutoOpen]);
    useEffect(() => {
      handleViewEventTrack();
    }, [visible]);
    return (
      <Modal
        title={
          <CounterOfferNotice
            title={t('landlord.long_tail.booking.counter_modal.title')}
            from="New Counter Offer Page"
          />
        }
        width={812}
        visible={visible}
        wrapClassName={classes.wrapper}
        destroyOnClose={true}
        okText={t('landlord.long_tail.booking.counter_modal.footer.confirm')}
        cancelButtonProps={{
          className: classes.cancelButton,
        }}
        okButtonProps={{
          className: classes.okButton,
          loading: instalmentLoading,
          disabled: isConfirmButtonDisabled,
        }}
        onOk={handleConfirmCreateOffer}
        onCancel={handleCloseModal}
      >
        <h5 className={classes.subTitle}>
          {t('landlord.long_tail.booking.counter_modal.subTitle', {
            name: record?.student?.firstName + ' ' + record?.student?.lastName,
          })}
        </h5>

        <CounterOffer
          t={t}
          property={record}
          showPerPrice
          showTotalPrice
          tagType={TagType.origin}
          showTagDesc={false}
        />

        <Form>
          <Row className={classes.settings} type="flex" gutter={32}>
            <Col span={24}>
              <h3>{t('landlord.long_tail.booking.counter_modal.setting.title')}</h3>
            </Col>

            <Col span={24}>
              <Form.Item
                label={
                  <div className={classes.labelWithNum}>
                    <span className={classes.labelNum}>1</span>
                    {t('landlord.long_tail.booking.counter_modal.form.room')}
                  </div>
                }
              >
                {getFieldDecorator('room', {
                  initialValue: roomOptionData?.length ? currentRoom : null,
                  rules: [
                    {
                      required: true,
                      message: t('landlord.long_tail.booking.counter_modal.form.room.required'),
                    },
                  ],
                })(
                  <Select
                    placeholder={t(
                      'landlord.long_tail.booking.counter_modal.form.room.placeholder',
                    )}
                    onChange={handleRoomChange}
                    dropdownRender={menu => (
                      <>
                        {menu}
                        <div style={{ margin: '0 12px' }}>
                          <Divider style={{ margin: 0 }} />
                        </div>
                        <div
                          className={classes.roomOptionExtra}
                          onMouseDown={e => e.preventDefault()}
                          onClick={handleOpenPropertyModal}
                        >
                          {t('landlord.long_tail.booking.counter_modal.form.room.extra.notice')}
                          &nbsp;
                          <span>
                            {t('landlord.long_tail.booking.counter_modal.form.room.extra.select')}
                          </span>
                        </div>
                      </>
                    )}
                  >
                    {roomOptionData?.map(room => {
                      return (
                        <Select.Option
                          key={room.id}
                          value={room.id}
                          onClick={() => {
                            handleClickCounterSetTrack('Select Room');
                          }}
                        >
                          <span className={classes.roomName}>{room.name}</span>
                          <span className={classes.roomCategory}>
                            {t(
                              `landlord.long_tail.booking.unit_type_category.${room.category?.toLowerCase()}`,
                            )}
                          </span>
                        </Select.Option>
                      );
                    })}
                  </Select>,
                )}
              </Form.Item>
            </Col>

            <If condition={reselectProperty}>
              <Col span={24}>
                <Alert
                  className={classes.alertWarning}
                  type="warning"
                  showIcon
                  message={
                    <>
                      <span>
                        {t(
                          'landlord.long_tail.booking.counter_modal.form.room.reselect_warning_first',
                        )}
                      </span>
                      <span className={classes.propertyName}> {reselectProperty?.name} </span>
                      <span>
                        {t(
                          'landlord.long_tail.booking.counter_modal.form.room.reselect_warning_second',
                        )}
                      </span>
                    </>
                  }
                />
              </Col>
            </If>

            <Col span={24}>
              <Form.Item
                label={
                  <div className={classes.labelWithNum}>
                    <span className={classes.labelNum}>2</span>
                    {t('landlord.long_tail.booking.counter_modal.form.listing')}
                  </div>
                }
              >
                {getFieldDecorator('listing', {
                  initialValue: listingOptionData ? currentListing : null,
                  rules: [
                    {
                      required: true,
                      message: t('landlord.long_tail.booking.counter_modal.form.listing.required'),
                    },
                  ],
                })(
                  <Select
                    className={classes.listing}
                    onChange={handleListingChange}
                    placeholder={t(
                      'landlord.long_tail.booking.counter_modal.form.listing.placeholder',
                    )}
                  >
                    {listingOptionData?.map(listing => {
                      let tenancyLengthValueMin;
                      let tenancyLengthValueMax;
                      if (listing.tenancyLengthValue && isNaN(Number(listing.tenancyLengthValue))) {
                        const [min, max] = listing.tenancyLengthValue.split('-');
                        [tenancyLengthValueMin, tenancyLengthValueMax] = [Number(min), Number(max)];
                      } else if (listing.tenancyLengthValue) {
                        tenancyLengthValueMin = Number(listing.tenancyLengthValue);
                      }

                      let currentPrice = listing.priceMin;
                      if (
                        listing.priceMin &&
                        listing.discountValue &&
                        listing.discountType === 'ABSOLUTE'
                      ) {
                        currentPrice = listing.priceMin - listing.discountValue;
                      } else if (
                        listing.priceMin &&
                        listing.discountValue &&
                        listing.discountType === 'PERCENTAGE'
                      ) {
                        // eslint-disable-next-line  no-mixed-operators
                        currentPrice = (listing.priceMin * (100 - listing.discountValue)) / 100;
                      }

                      const tenancy = handleTenancy(
                        listing.moveIn,
                        listing.moveOut,
                        listing.moveInType,
                        listing.moveOutType,
                        record.billingCycle,
                        listing.tenancyLengthType,
                        tenancyLengthValueMin,
                        tenancyLengthValueMax,
                      );

                      return (
                        <Select.Option
                          key={listing.id}
                          value={listing.id}
                          dropdownClassName={classes.listingDropdown}
                          onClick={() => {
                            handleClickCounterSetTrack('Listing');
                          }}
                        >
                          <div className={classes.listingInfo}>
                            <div className={classes.listingDate}>
                              <div>
                                {t('cms:cms.listing.sub_table.move_in_label', {
                                  moveInDesc: tenancy.moveIn || '--',
                                })}
                              </div>
                              <div>
                                {t('cms:cms.listing.sub_table.move_out_label', {
                                  moveOutDesc: tenancy.moveOut || '--',
                                })}
                              </div>
                            </div>

                            <span className={classes.listingDivider}>|</span>

                            <div className={classes.listingTenancy}>
                              <span className={classes.listingSpan}>{tenancy.stay || '--'}</span>
                            </div>

                            <span className={classes.listingDivider}>|</span>

                            <div className={classes.listingPrice}>
                              {formatPrice(currentPrice, record.currency) || '--'}
                              <If
                                condition={
                                  listing.discountType && listing.discountType !== 'NO_DISCOUNT'
                                }
                              >
                                <span className="listing-detail__sub-column-content--origin-price">
                                  {formatPrice(listing.priceMin, record.currency)}
                                </span>
                              </If>

                              <span className={classes.listingBillingCycle}>
                                / {propertyBillingCycle}
                              </span>
                            </div>

                            <span className={classes.listingDivider}>|</span>

                            <div
                              className={`${
                                classes.listingAvailability
                              } ${listing.availability?.toLowerCase()}`}
                            >
                              {t(
                                `cms:cms.listing.modal.availability.option.${
                                  listing.availability?.toLowerCase() ?? 'good'
                                }`,
                              )}
                            </div>
                          </div>
                        </Select.Option>
                      );
                    })}
                  </Select>,
                )}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <div className={classes.labelWithNum}>
                    <span className={classes.labelNum}>3</span>
                    {t('landlord.long_tail.booking.counter_modal.form.move_in_date')}
                  </div>
                }
                style={{ marginBottom: isDateModified ? 8 : 24 }}
              >
                {getFieldDecorator('moveIn', {
                  rules: [
                    {
                      required: true,
                      message: (
                        <div style={{ marginTop: 4 }}>
                          {t('landlord.long_tail.booking.counter_modal.form.move_in_date.required')}
                        </div>
                      ),
                    },
                    {
                      validator: (_, value, callback) => {
                        if (value && value.isBefore(moment().format(dayFormatType.FILTERPARAMS))) {
                          return callback(
                            t('landlord.long_tail.booking.counter_modal.form.date.error'),
                          );
                        }

                        return callback();
                      },
                    },
                  ],
                })(
                  <DatePicker
                    style={{ width: '100%', height: '32px', borderRadius: '2px' }}
                    format="ll"
                    showToday={false}
                    defaultPickerValue={
                      moment(listingData?.moveIn)?.isBefore(
                        moment().format(dayFormatType.FILTERPARAMS),
                      )
                        ? moment()
                        : moment(listingData?.moveIn)
                    }
                    onChange={handleMoveInChange}
                    disabledDate={getMoveInDisabledDate}
                  />,
                )}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <div className={classes.labelWithNum}>
                    <span className={classes.labelNum}>4</span>
                    {t('landlord.long_tail.booking.counter_modal.form.move_out_date')}
                  </div>
                }
                style={{ marginBottom: isDateModified ? 8 : 24 }}
              >
                {getFieldDecorator('moveOut', {
                  rules: [
                    {
                      required: true,
                      message: t(
                        'landlord.long_tail.booking.counter_modal.form.move_out_date.required',
                      ),
                    },
                    {
                      validator: (_, value, callback) => {
                        if (value && value.isBefore(moment().format(dayFormatType.FILTERPARAMS))) {
                          return callback(
                            t('landlord.long_tail.booking.counter_modal.form.date.error'),
                          );
                        }

                        return callback();
                      },
                    },
                  ],
                })(
                  <DatePicker
                    style={{ width: '100%', height: '32px', borderRadius: '2px' }}
                    format="ll"
                    showToday={false}
                    defaultPickerValue={getMoveOutDisabledDate()}
                    disabled={!getFieldValue('moveIn')}
                    onChange={handleMoveOutChange}
                    disabledDate={getMoveOutDisabledDate}
                  />,
                )}
              </Form.Item>
            </Col>

            <If condition={isDateModified}>
              <Col span={24}>
                <Alert
                  className={classes.alertWarning}
                  type="warning"
                  showIcon
                  message={
                    <>
                      <p>{t('landlord.long_tail.booking.counter_modal.form.warning')}</p>
                      <p className={classes.warningDate}>
                        <span>
                          {t('landlord.long_tail.booking.counter_modal.form.warning.msg')}
                        </span>
                        <span>
                          &nbsp;
                          {moment(record.moveInDate)?.format(dayFormatType.BOOKINGTABLE)}
                          &nbsp;-&nbsp;
                          {moment(record.moveOutDate)?.format(dayFormatType.BOOKINGTABLE)}
                          &nbsp;
                        </span>
                        <span className={classes.listingDivider}>|</span>
                        <span>{calcTenancyLength(t, record.moveInDate, record.moveOutDate)}</span>
                      </p>
                    </>
                  }
                />
              </Col>
            </If>
            <div className={classes.divider}>
              <Divider />
            </div>

            <Col span={12}>
              <Form.Item label={t('landlord.long_tail.booking.counter_modal.form.discount_type')}>
                {getFieldDecorator('discountType', {
                  initialValue: null,
                })(
                  <Select onChange={handleDiscountTypeChange}>
                    <Select.Option
                      value={null}
                      onClick={() => {
                        handleClickCounterSetTrack('Discount Type');
                      }}
                    >
                      {t('landlord.long_tail.booking.counter_modal.form.discount_type.no_discount')}
                    </Select.Option>
                    <Select.Option
                      value={orderCounterOfferDiscountType.PERCENTAGE}
                      onClick={() => {
                        handleClickCounterSetTrack('Discount Type');
                      }}
                    >
                      {t(
                        `landlord.long_tail.booking.counter_modal.form.discount_type.${orderCounterOfferDiscountType.PERCENTAGE?.toLocaleLowerCase()}`,
                      )}
                    </Select.Option>
                    <Select.Option
                      value={orderCounterOfferDiscountType.ABSOLUTE}
                      onClick={() => {
                        handleClickCounterSetTrack('Discount Type');
                      }}
                    >
                      {t(
                        `landlord.long_tail.booking.counter_modal.form.discount_type.${orderCounterOfferDiscountType.ABSOLUTE?.toLocaleLowerCase()}`,
                      )}
                    </Select.Option>
                  </Select>,
                )}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={t('landlord.long_tail.booking.counter_modal.form.discount_value')}>
                {getFieldDecorator('discountValue', {
                  required: true,
                  rules: [
                    {
                      validator: (_, value, callback) => {
                        if (
                          getFieldValue('discountType') === orderCounterOfferDiscountType.ABSOLUTE
                        ) {
                          if (
                            value &&
                            (Number(value) > Number(getFieldValue('originalPrice')) ||
                              Number(value) < 0)
                          ) {
                            return callback(
                              t(
                                'landlord.long_tail.booking.counter_modal.form.discount_value.error',
                              ),
                            );
                          }

                          return callback();
                        }

                        return callback();
                      },
                    },
                  ],
                })(
                  <InputNumber
                    min={0}
                    max={
                      getFieldValue('discountType') === orderCounterOfferDiscountType.PERCENTAGE
                        ? 100
                        : Infinity
                    }
                    formatter={value =>
                      getFieldValue('discountType') === orderCounterOfferDiscountType.PERCENTAGE
                        ? `${value}%`
                        : value
                    }
                    parser={value =>
                      getFieldValue('discountType') === orderCounterOfferDiscountType.PERCENTAGE
                        ? value.replace('%', '')
                        : value
                    }
                    disabled={getFieldValue('discountType') === null}
                    onChange={handleDiscountValueChangeDebounce}
                    onClick={() => {
                      handleClickCounterSetTrack('Discount Value');
                    }}
                  />,
                )}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('landlord.long_tail.booking.counter_modal.form.original_price', {
                  currency: record.currency,
                })}
              >
                {getFieldDecorator('originalPrice')(
                  <Input disabled addonAfter={propertyBillingCycle} />,
                )}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('landlord.long_tail.booking.counter_modal.form.current_price', {
                  currency: record.currency,
                })}
              >
                {getFieldDecorator('currentPrice')(
                  <Input disabled addonAfter={propertyBillingCycle} />,
                )}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('landlord.long_tail.booking.counter_modal.form.total_price', {
                  currency: record.currency,
                })}
              >
                {getFieldDecorator('totalPrice')(<Input disabled />)}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('landlord.long_tail.booking.counter_modal.form.offer_validity_period')}
              >
                {getFieldDecorator('validPeriodHour', {
                  initialValue: 24,
                })(
                  <Select>
                    {OFFER_VALIDITY_PERIOD.map(num => (
                      <Select.Option
                        value={num}
                        onClick={() => {
                          handleClickCounterSetTrack('Offer Validity Period');
                        }}
                      >
                        {t(
                          'landlord.long_tail.booking.counter_modal.form.offer_validity_period.option',
                          { num },
                        )}
                      </Select.Option>
                    ))}
                  </Select>,
                )}
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={t('landlord.long_tail.booking.counter_modal.form.leave_message')}
                style={{ marginBottom: 32 }}
              >
                {getFieldDecorator('message', {
                  required: true,
                })(
                  <Input.TextArea
                    maxLength={200}
                    onClick={() => {
                      handleClickCounterSetTrack('Message');
                    }}
                  />,
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <SecondConfirmModal
          ref={confirmModalRef}
          record={record}
          counterOffer={counterOffer}
          handleCloseModal={handleCloseModal}
          refetchBookingList={refetchBookingList}
        />

        <SelectPropertyModal
          ref={selectPropertyRef}
          booking={record}
          handleOpenCounterOffer={handleReOpenModal}
        />
      </Modal>
    );
  }),
);

export default Form.create()(CounterOfferModal);
